import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import placeholderImage from "../../../assets/placeholder.png";
import { ActionKind } from "../../types/Action";
import { ProductPreset } from "../../types/ProductPreset";
import { translate } from "../../utils/translations";
import { FadeIn, SlideUp } from "../Animations";
import { Button } from "../Button";
import { useConfig } from "../ConfigProvider";
import {
    RootStateKind,
    ScanSubStateKind,
    useRootState
} from "../state/StateProvider";

const ProductPickerDialog = ({
    productPreset,
    onCancel,
    onSelect
}: {
    productPreset: ProductPreset;
    onCancel: () => void;
    onSelect: (code: string) => void;
}) => {
    const { t } = useTranslation();
    const {
        state: { accessibilityMode }
    } = useRootState(RootStateKind.Scan);

    const { i18n } = useTranslation();

    const [currentFilter, setFilter] = useState<number>(0);

    const products = productPreset.categories[currentFilter].products;

    const FilterTab = ({ index }: { index: number }) => {
        return (
            <Button
                variant={
                    index === currentFilter
                        ? ("primary" as const)
                        : ("secondary" as const)
                }
                onClick={() => {
                    setFilter(index);
                }}
                className="flex-1 p-2 text-center text-xs capitalize"
            >
                {translate(productPreset.categories[index].translations, i18n)}
            </Button>
        );
    };

    return (
        <>
            <div
                data-testid="productPicker"
                className="z-10 flex h-full flex-col gap-3 bg-primary-100 p-3"
            >
                <h1 className="text-3xl font-semibold leading-snug text-primary-800">
                    {translate(productPreset.translations, i18n)}
                </h1>
                <div className="flex items-stretch gap-2">
                    {productPreset.categories.length > 1 &&
                        productPreset.categories.map((_, index) => (
                            <FilterTab key={index} index={index} />
                        ))}
                </div>
                <div className="-mx-3 flex-1 overflow-y-auto">
                    <div
                        data-testid="products"
                        className={`grid ${
                            accessibilityMode ? "grid-cols-3" : "grid-cols-4"
                        } gap-2 px-3`}
                    >
                        {products.map((product, i) => (
                            <Button
                                data-testid={"fixtasten".concat(product.ean)}
                                key={`${translate(
                                    product.translations,
                                    i18n
                                )}-${i}`}
                                onClick={() => onSelect(product.ean)}
                                className={`grid place-items-center px-4 py-2 ${
                                    accessibilityMode
                                        ? "grid-cols-2 gap-x-1"
                                        : "grid-cols-1"
                                } bg-white text-center text-xs font-semibold text-primary-800`}
                            >
                                <img
                                    src={product.imageUrl ?? placeholderImage}
                                    className="h-12 object-contain"
                                />
                                <span className="w-full overflow-hidden text-ellipsis capitalize">
                                    {translate(
                                        product.translations,
                                        i18n
                                    ).toLowerCase()}
                                </span>
                            </Button>
                        ))}
                    </div>
                </div>
                <Button
                    variant="primary"
                    className="flex-none text-center leading-loose"
                    onClick={() => onCancel()}
                >
                    {t("back")}
                </Button>
            </div>
        </>
    );
};

export const ProductPicker = () => {
    const { state, dispatch } = useRootState(RootStateKind.Scan);
    const { productPresets } = useConfig();

    const { subState, accessibilityMode } = state;

    const presetImages = productPresets.flatMap((pp) => [
        pp.imageUrl,
        ...pp.categories.flatMap((c) => c.products.map((p) => p.imageUrl))
    ]);

    return (
        <AnimatePresence>
            {presetImages.map((image, i) =>
                image ? (
                    <link
                        key={`preset-${i}`}
                        rel="preload"
                        as="image"
                        href={image}
                    />
                ) : null
            )}
            {subState.kind === ScanSubStateKind.ProductPicker && (
                <>
                    <FadeIn className="pt-30 fixed inset-0 bg-primary-900 bg-opacity-75" />
                    <SlideUp
                        key="select"
                        className={`fixed inset-x-0 bottom-0 ${
                            accessibilityMode ? "top-80" : "top-12"
                        } z-10 rounded-t-xl`}
                    >
                        <ProductPickerDialog
                            productPreset={subState.productPreset}
                            onSelect={(barcode) => {
                                dispatch({
                                    kind: ActionKind.AddProduct,
                                    payload: {
                                        kind: "manuallyAdded",
                                        code: barcode,
                                        analyticsSource: "fixtasten"
                                    }
                                });
                            }}
                            onCancel={() =>
                                dispatch({ kind: ActionKind.HideProducts })
                            }
                        />
                    </SlideUp>
                </>
            )}
        </AnimatePresence>
    );
};
